import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";
import { format, parseJSON } from "date-fns";
import { Box, Divider, Link, Typography, useTheme } from "@mui/material";
import { TransactionV2 } from "@rentablo/api/apiv1";
import {
  BOTTOM_SHEET_Z_INDEX,
  GainLossTypography,
  Modal,
} from "@rentablo/components";
import { toCurrency } from "@helpers";
import { TransactionStatusIcon } from "@features/investments/investment/components/TransactionStatusIcon";
import { IntervalInfo } from "./IntervalInfo";

const CENTS = { minimumFractionDigits: 2 };

const formatDate = (date?: string) =>
  date ? format(parseJSON(date), "dd.MM.yyyy") : undefined;

const Detail = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Box py={2.5}>
    <Typography variant="caption" color="textSecondary" component="div">
      {label}
    </Typography>
    <Typography
      variant="body3"
      component="div"
      mt={0.5}
      sx={{ overflowWrap: "anywhere" }}
    >
      {children}
    </Typography>
  </Box>
);

interface Props {
  transaction?: TransactionV2;
  title?: string;
  currency?: string;
  onClose: () => void;
}

export const AccountTransactionSheet = ({
  transaction,
  title,
  currency = "EUR",
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const booking = transaction?.booking;
  const status = booking && {
    dataConsistencyStatus: booking.dataConsistencyStatus,
    dataSource: booking.dataSource,
    isExternalID: Boolean(booking.externalId),
  };
  const counterpart = [
    transaction?.counterpartName,
    transaction?.counterpartIban,
  ].filter(Boolean);

  const details = transaction && [
    {
      label: t("singleAccountPage.protocolTable.headers.valueDate"),
      value: (
        <Box component="span" display="flex" alignItems="center" gap={1.5}>
          {formatDate(transaction.valueDate)}
          {status && <TransactionStatusIcon {...status} size={16} />}
        </Box>
      ),
    },
    {
      label: t("singleAccountPage.protocolTable.headers.bookingDate"),
      value: formatDate(transaction.bankBookingDate),
    },
    {
      label: t("singleAccountPage.protocolTable.details.counterpart"),
      value: counterpart.length ? counterpart.join(" · ") : undefined,
    },
    {
      label: t("singleAccountPage.protocolTable.details.purpose"),
      value: transaction.isAdjustingEntry
        ? t("singleAccountPage.protocolTable.adjustingEntryText")
        : transaction.purpose,
    },
    {
      label: t("singleAccountPage.protocolTable.details.type"),
      value: transaction.literalType,
    },
    {
      label: t("singleAccountPage.protocolTable.details.investment"),
      value: booking && (
        <Link
          component={RouterLink}
          to={`/investments/${booking.investment.id}`}
          onClick={onClose}
        >
          {booking.investment.name}
        </Link>
      ),
    },
  ];

  return (
    <Modal
      isOpen={transaction !== undefined}
      onClose={onClose}
      title={title}
      testId="account-transaction-sheet"
      position="bottom"
      width="100%"
      height="fit-content"
      zIndex={BOTTOM_SHEET_Z_INDEX}
      disablePortal={false}
      contentPadding={theme.spacing(2, 4, 6)}
      titleMargin={2}
    >
      {transaction && (
        <>
          <GainLossTypography value={transaction.amount} variant="h4">
            {toCurrency(transaction.amount, currency, false, CENTS)}
          </GainLossTypography>
          <IntervalInfo
            isRegular={transaction.isRegular}
            paymentInterval={transaction.paymentInterval}
            isInterAccountTransfer={transaction.isInterAccountTransfer}
            isAdjustingEntry={transaction.isAdjustingEntry}
          />
          <Box mt={2}>
            {details
              ?.filter(({ value }) => Boolean(value))
              .map(({ label, value }, index) => (
                <React.Fragment key={label}>
                  {index > 0 && <Divider />}
                  <Detail label={label}>{value}</Detail>
                </React.Fragment>
              ))}
          </Box>
        </>
      )}
    </Modal>
  );
};
